<script setup lang="ts">
import { onMounted } from 'vue';

import { usePageConnectorStore } from '@apps/main/new-desktop/core/stores/pageConnectorStore';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import type { TransportType } from '@libs/trip-planner-interfaces';

import type { ItineraryFerrySegment } from '../../../models';
import { isAvailableItineraryFerrySegment } from '../../../models';
import {
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
} from '../../../stores';

import AvailableFerrySegment from './AvailableFerrySegment.vue';
import UnavailableFerrySegment from './UnavailableFerrySegment.vue';

// NOTE: Important:
// * this component is smart
// * keep child components of this one dumb
// * every action in this component must be guarded by print mode flag

interface ItineraryFerrySegmentProps {
  segment: ItineraryFerrySegment;
  printMode: boolean;
}

const props = defineProps<ItineraryFerrySegmentProps>();

const pageConnectorStore = usePageConnectorStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const ferryFiltersStore = useFerryFiltersStore();
const statsigEventsStore = useStatsigEventsStore();

onMounted(() => {
  // Log transport static info not available event to Statsig
  if (
    isAvailableItineraryFerrySegment(props.segment) &&
    props.segment.productCardData?.details.logo.urls.length === 0 &&
    props.segment.productCardData?.details.carrier
  ) {
    statsigEventsStore.logTransportStaticInfoNotAvailable(
      props.segment.productCardData?.details.carrier
    );
  }
});

function updateSuggestionType(suggestionType: SuggestionType): void {
  if (props.printMode) {
    return;
  }
  tripOverviewPageStore.updateSuggestionType(
    props.segment.ingredientId,
    suggestionType
  );
}

function openFiltersModal(): void {
  tripOverviewPanelsStore.openFerryFiltersPanel(props.segment.ingredientId);
}

function removeFilter(tagId: string): void {
  ferryFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}

function changeUpsell(): void {
  tripOverviewModalsStore.openFerryMultipleVehiclesModal(
    props.segment.ingredientId
  );
}

function showDetails(): void {
  tripOverviewPageStore.showFerryDetailsModal(props.segment.ingredientId);
}

function selectTransport(transportType: TransportType): void {
  tripOverviewPageStore.selectTransport(props.segment, transportType);
}
</script>

<template>
  <AvailableFerrySegment
    v-if="isAvailableItineraryFerrySegment(props.segment)"
    :segment="props.segment"
    :print-mode="props.printMode"
    @change-upsell="changeUpsell"
    @remove-filter="removeFilter"
    @select-transport="selectTransport"
    @show-details="showDetails"
    @show-more-filters="openFiltersModal"
    @suggestion-type-selected="updateSuggestionType"
  />
  <UnavailableFerrySegment
    v-else
    :segment="props.segment"
    :print-mode="props.printMode"
    :unavailability-message="
      tripOverviewPageStore.getUnavailabilityMessage(
        props.segment.segmentType,
        props.segment.reasons
      )
    "
    @edit-filters="pageConnectorStore.openFerryListFilters()"
    @remove-filter="removeFilter"
    @select-transport="selectTransport"
    @show-more-filters="openFiltersModal"
    @suggestion-type-selected="updateSuggestionType"
    @try-another-date="tripOverviewPageStore.scrollToCalendar()"
  />
</template>
